import React from 'react';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { BenefitRequest } from '../../../models';
import { APPROVE_OR_DECLINE_BENEFIT_REQUEST } from '../../../graphql/mutation';
import { useNotify, useUser } from '../../../hooks';
import RequestFormContent from './RequestFormContent';
import RequestFormButtons from './RequestFormButtons';
import { roles } from '../../../static';

interface RequestFormProps {
  request: BenefitRequest;
}

function RequestForm({ request }: RequestFormProps) {
  const router = useRouter();
  const notify = useNotify();
  const [user] = useUser();

  const [disable, setDisable] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<BenefitRequest>>({
    createdAt: dayjs(request?.createdAt).format('DD-MM-YYYY'),
    amountRequested: request?.amountRequested,
    amountApproved: request?.amountApproved ?? 0,
    status: request?.status,
    active: request?.active,
  });

  const [approveOrDeclineBenefitMutation] = useMutation<{
    approveOrDeclineBenefitRequest: BenefitRequest;
  }>(APPROVE_OR_DECLINE_BENEFIT_REQUEST);

  const updateRequest = async (state: Partial<BenefitRequest>) => {
    try {
      if (disable) return;
      setDisable(true);

      // validate form
      if (formState.status === 'approved') {
        if (formState.amountApproved > request?.amountRequested) {
          return notify(
            'El monto aprobado no puede ser mayor al monto solicitado',
            'error'
          );
        }

        if (formState.amountApproved <= 0) {
          return notify('El monto aprobado no puede ser negativo', 'error');
        }
      }

      const { data } = await approveOrDeclineBenefitMutation({
        variables: {
          data: {
            companyId: request?.company?._id,
            amountApproved: Number(state.amountApproved),
            benefitRequestId: request?._id,
          },
        },
      });
      if (data?.approveOrDeclineBenefitRequest?._id) {
        notify('Solicitud actualizada con éxito', 'success');
        const path =
          user?.userType === roles.admin ? '/app/receivable' : '/app/requests';

        router.push(path);
      } else {
        notify('No se pudo actualizar la solicitud', 'error');
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setDisable(false);
    }
  };

  return (
    <div className="flex flex-col w-full space-y-10">
      <RequestFormContent
        request={request}
        formState={formState}
        setFormState={setFormState}
      />
      <RequestFormButtons
        updateRequest={updateRequest}
        formState={formState}
        request={request}
      />
    </div>
  );
}

export default RequestForm;
